import type { ReactNode } from 'react'
import { Surface } from '@heroui/react'
import { useQuery } from '@tanstack/react-query'
import { Link, Outlet, useLocation } from '@tanstack/react-router'
import { Activity } from 'lucide-react'
import { useLocale, useTranslations } from 'use-intl'
import { LocaleSwitcher } from '~/components/common/locale-switcher'
import { ModeToggle } from '~/components/common/mode-toggle'
import { ComplianceNotice } from '~/components/layout/compliance-notice'
import { EmailVerificationBanner } from '~/features/compliance/email-verification-banner'
import {
  footballCoreHref,
  footballCoreLocale,
} from '~/lib/navigation/football-core'
import { productNavItems } from '~/lib/navigation/routes'
import { createSessionQuery } from '~/lib/session-query'

function classNames(...values: Array<string | false | undefined>) {
  return values.filter(Boolean).join(' ')
}

function isActiveProductPath(pathname: string, target: string) {
  return pathname === target || pathname.startsWith(`${target}/`)
}

export function ProductShellLayout({ children }: { children?: ReactNode }) {
  const t = useTranslations('ProductShell')
  const locale = footballCoreLocale(useLocale())
  const location = useLocation()
  const { data } = useQuery(createSessionQuery())
  const isAdmin = data?.user?.role === 'admin'
  const navItems = productNavItems.map(item => ({
    ...item,
    label: t(`nav.${item.labelKey}`),
  }))

  return (
    <div className="min-h-screen bg-background">
      <Surface variant="default" className="sticky top-0 z-40 border-b border-divider/60 bg-background/90 backdrop-blur">
        <div className="mx-auto flex min-h-16 max-w-7xl flex-col gap-3 px-4 py-3 lg:flex-row lg:items-center lg:justify-between lg:px-6">
          <div className="flex min-w-0 items-center justify-between gap-3">
            <a href={footballCoreHref(locale, '/')} className="flex min-w-0 items-center gap-3">
              <div className="flex size-9 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary">
                <Activity className="size-5" aria-hidden="true" />
              </div>
              <div className="min-w-0">
                <div className="truncate text-lg font-black tracking-normal" translate="no">BallPeek</div>
                <div className="truncate text-xs text-muted">{t('subtitle')}</div>
              </div>
            </a>
            <div className="flex shrink-0 items-center gap-2 lg:hidden">
              <ModeToggle />
              <LocaleSwitcher />
            </div>
          </div>

          <div className="flex min-w-0 flex-wrap items-center justify-between gap-3">
            <nav className="flex min-w-0 gap-2 overflow-x-auto" aria-label={t('nav.ariaLabel')}>
              {navItems.map((item) => {
                const Icon = item.icon
                const localizedTo = footballCoreHref(locale, item.to)
                const isActive = isActiveProductPath(location.pathname, item.to) || isActiveProductPath(location.pathname, localizedTo)
                return (
                  <a
                    key={item.to}
                    href={localizedTo}
                    className={classNames(
                      'inline-flex min-h-11 items-center gap-2 rounded-md px-3 text-sm font-semibold whitespace-nowrap',
                      isActive ? 'bg-primary text-primary-foreground' : 'text-muted hover:bg-default-100 hover:text-foreground',
                    )}
                  >
                    <Icon className="size-4" aria-hidden="true" />
                    {item.label}
                  </a>
                )
              })}
            </nav>

            <div className="flex shrink-0 items-center gap-2">
              <div className="hidden items-center gap-2 lg:flex">
                <ModeToggle />
                <LocaleSwitcher />
              </div>
              {isAdmin && (
                <Link
                  to="/admin/mappings"
                  search={{}}
                  className="inline-flex min-h-11 items-center rounded-md border border-divider px-3 text-sm font-semibold text-muted transition hover:bg-default-100 hover:text-foreground"
                >
                  {t('admin')}
                </Link>
              )}
            </div>
          </div>
        </div>
      </Surface>
      {data?.user && !data.user.emailVerified && <EmailVerificationBanner email={data.user.email} />}

      <main className="mx-auto max-w-7xl px-4 py-6 lg:px-6">
        {children ?? <Outlet />}
      </main>
      <ComplianceNotice compact />
    </div>
  )
}
